import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { RealtimeChannel } from "@supabase/supabase-js";

interface CreditLimits {
  dailyLimit: number;
  monthlyLimit: number;
  allowedModels: string[];
}

interface CreditUsage {
  dailyUsed: number;
  monthlyUsed: number;
  dailyRemaining: number;
  monthlyRemaining: number;
  dailyPercentage: number;
  monthlyPercentage: number;
  isNearDailyLimit: boolean;
  isNearMonthlyLimit: boolean;
}

type AlertLevel = 'safe' | 'warning' | 'danger';

export function useRealtimeCredits() {
  const { user, profile } = useAuth();
  const [limits, setLimits] = useState<CreditLimits | null>(null);
  const [usage, setUsage] = useState<CreditUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);

  const fetchCreditsData = useCallback(async () => {
    if (!user) return;

    try {
      console.log('Fetching credits data for user:', user.id);

      // Buscar limites do plano
      const { data: limitsData, error: limitsError } = await supabase
        .rpc('get_user_credit_limits', { p_user_id: user.id });

      if (limitsError) {
        console.error('Error fetching limits:', limitsError);
        throw limitsError;
      }

      const creditLimits: CreditLimits = {
        dailyLimit: limitsData[0]?.daily_limit || 10000,
        monthlyLimit: limitsData[0]?.monthly_limit || 300000,
        allowedModels: limitsData[0]?.allowed_models || ['google/gemini-2.5-flash'],
      };

      setLimits(creditLimits);

      // Buscar uso diário
      const { data: dailyData, error: dailyError } = await supabase
        .rpc('get_user_daily_tokens', { p_user_id: user.id });

      if (dailyError) {
        console.error('Error fetching daily usage:', dailyError);
        throw dailyError;
      }

      const dailyUsed = dailyData || 0;

      // Buscar uso mensal
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      const { data: monthlyData, error: monthlyError } = await supabase
        .rpc('get_user_monthly_tokens', { 
          p_user_id: user.id,
          p_year: currentYear,
          p_month: currentMonth
        });

      if (monthlyError) {
        console.error('Error fetching monthly usage:', monthlyError);
        throw monthlyError;
      }

      const monthlyUsed = monthlyData || 0;

      // Calcular métricas
      const dailyRemaining = Math.max(0, creditLimits.dailyLimit - dailyUsed);
      const monthlyRemaining = Math.max(0, creditLimits.monthlyLimit - monthlyUsed);
      const dailyPercentage = Math.min(100, (dailyUsed / creditLimits.dailyLimit) * 100);
      const monthlyPercentage = Math.min(100, (monthlyUsed / creditLimits.monthlyLimit) * 100);

      const newUsage: CreditUsage = {
        dailyUsed,
        monthlyUsed,
        dailyRemaining,
        monthlyRemaining,
        dailyPercentage,
        monthlyPercentage,
        isNearDailyLimit: dailyPercentage >= 80,
        isNearMonthlyLimit: monthlyPercentage >= 80,
      };

      console.log('Credits data updated:', newUsage);
      setUsage(newUsage);

    } catch (error) {
      console.error('Error fetching credits data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Setup Realtime subscription
  useEffect(() => {
    if (!user || !profile) {
      setIsLoading(false);
      return;
    }

    // Initial fetch
    fetchCreditsData();

    // Setup Realtime channel
    console.log('Setting up Realtime channel for ai_usage_metrics');
    const realtimeChannel = supabase
      .channel('credits-updates')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'ai_usage_metrics',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Realtime update received:', payload);
          // Refetch data when changes occur
          fetchCreditsData();
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
      });

    setChannel(realtimeChannel);

    // Cleanup
    return () => {
      console.log('Cleaning up Realtime channel');
      if (realtimeChannel) {
        supabase.removeChannel(realtimeChannel);
      }
    };
  }, [user, profile, fetchCreditsData]);

  const formatTokens = useCallback((tokens: number): string => {
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}k`;
    return tokens.toString();
  }, []);

  const getAlertLevel = useCallback((percentage: number): AlertLevel => {
    if (percentage >= 90) return 'danger';
    if (percentage >= 80) return 'warning';
    return 'safe';
  }, []);

  return {
    limits,
    usage,
    isLoading,
    formatTokens,
    getAlertLevel,
    refresh: fetchCreditsData,
  };
}
